import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

/**
 * Validate email format
 * @param {string} email 
 * @returns {boolean}
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const Unsubscribe = () => {
  const { token } = useParams();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, already
  const [message, setMessage] = useState('');

  // Auto-unsubscribe if token is present
  useEffect(() => {
    if (token) {
      unsubscribeWithToken(token);
    }
  }, [token]);

  const unsubscribeWithToken = async (unsubscribeToken) => {
    setStatus('loading');

    try {
      const response = await fetch(`/api/newsletter/unsubscribe/${unsubscribeToken}`);
      const data = await response.json();

      if (data.alreadyUnsubscribed) {
        setStatus('already');
        setMessage('You\'re already unsubscribed.');
      } else if (!data.success) {
        setStatus('error');
        setMessage(data.error || 'Something went wrong. Please try again.');
      } else {
        setStatus('success');
        setMessage(data.message || 'You have been successfully unsubscribed.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      setStatus('error');
      setMessage('Please enter your email address');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    
    try {
      const response = await fetch('/api/newsletter/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: trimmedEmail,
          newsletter_type: 'ai_notes'
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.alreadyUnsubscribed) {
          setStatus('already');
        } else {
          setStatus('success');
        }
        setMessage(data.message);
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  // If we have a token and are loading, show loading state
  if (token && status === 'loading') {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Unsubscribing... - AI Notes</title>
        </Helmet>
        <div style={{
          background: 'linear-gradient(135deg, rgba(232, 232, 232, 0.92) 0%, rgba(248,250,252,0.95) 100%)',
          minHeight: 'auto',
          padding: '100px 20px 40px 20px',
          margin: '0',
          width: '100%'
        }}>
          <div style={{ width: '100%', maxWidth: 'none' }}>
            {/* Header Section */}
            <Row className="justify-content-center mb-5">
              <Col lg={8} className="text-center">
                <h1 style={{
                  fontSize: '2.5rem',
                  fontWeight: '300',
                  color: '#2d3748',
                  marginBottom: '0',
                  fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
                }}>
                  Unsubscribing...
                </h1>
              </Col>
            </Row>

            {/* Loading Card */}
            <Row className="justify-content-center">
              <Col lg={8} md={10}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
                  borderRadius: '16px',
                  padding: '2rem',
                  boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  border: 'none',
                  textAlign: 'center'
                }}>
                  <div className="spinner-border" role="status" style={{ color: '#667eea' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p style={{
                    marginTop: '1rem',
                    marginBottom: 0,
                    color: '#718096',
                    fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
                  }}>
                    Processing your request...
                  </p>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </>
    );
  }

  // Success or already unsubscribed state
  if (status === 'success' || status === 'already') {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Unsubscribed - AI Notes</title>
        </Helmet>
        <div style={{
          background: 'linear-gradient(135deg, rgba(232, 232, 232, 0.92) 0%, rgba(248,250,252,0.95) 100%)',
          minHeight: 'auto',
          padding: '100px 20px 40px 20px',
          margin: '0',
          width: '100%'
        }}>
          <div style={{ width: '100%', maxWidth: 'none' }}>
            {/* Header Section */}
            <Row className="justify-content-center mb-5">
              <Col lg={8} className="text-center">
                <h1 style={{
                  fontSize: '2.5rem',
                  fontWeight: '300',
                  color: '#2d3748',
                  marginBottom: '0',
                  fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
                }}>
                  {status === 'already' ? 'Already Unsubscribed' : 'You\'ve Been Unsubscribed'}
                </h1>
              </Col>
            </Row>

            {/* Confirmation Card */}
            <Row className="justify-content-center">
              <Col lg={8} md={10}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
                  borderRadius: '16px',
                  padding: '2rem',
                  boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  border: 'none'
                }}>
                  {/* Message */}
                  <div className="text-center mb-4">
                    <div style={{
                      color: '#4a5568',
                      fontSize: '0.95rem',
                      fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                      lineHeight: '1.6'
                    }}>
                      {status === 'already'
                        ? 'You\'re already unsubscribed from the AI Notes emails.'
                        : 'You\'ll no longer receive the AI Notes emails.'
                      }
                    </div>
                  </div>

                  {/* Resubscribe info */}
                  <div className="text-center" style={{
                    color: '#718096',
                    fontSize: '0.95rem',
                    fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
                  }}>
                    Changed your mind?{' '}
                    <Link
                      to="/ai-notes"
                      style={{ color: '#667eea', textDecoration: 'none' }}
                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                      Visit AI Notes
                    </Link>
                    {' '}to resubscribe.
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </>
    );
  }

  // Form state (no token, or token failed)
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Unsubscribe - AI Notes</title>
      </Helmet>
      <div style={{
        background: 'linear-gradient(135deg, rgba(232, 232, 232, 0.92) 0%, rgba(248,250,252,0.95) 100%)',
        minHeight: 'auto',
        padding: '100px 20px 40px 20px',
        margin: '0',
        width: '100%'
      }}>
        <div style={{ width: '100%', maxWidth: 'none' }}>
          {/* Header Section */}
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '300',
                color: '#2d3748',
                marginBottom: '0',
                fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
              }}>
                Unsubscribe from the AI Notes
              </h1>
            </Col>
          </Row>

          {/* Form Card */}
          <Row className="justify-content-center">
            <Col lg={8} md={10}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
                borderRadius: '16px',
                padding: '2rem',
                boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                border: 'none'
              }}>
                {/* Message */}
                <div className="text-center mb-4">
                  <div style={{
                    color: '#4a5568',
                    fontSize: '0.95rem',
                    fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                    lineHeight: '1.6'
                  }}>
                    Enter your email address to unsubscribe.
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleEmailSubmit}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === 'error') setStatus('idle');
                    }}
                    placeholder="your@email.com"
                    disabled={status === 'loading'}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      fontSize: '1rem',
                      border: status === 'error' ? '1px solid #fc8181' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      outline: 'none',
                      marginBottom: '1rem',
                      fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = status === 'error' ? '#fc8181' : '#e2e8f0'}
                  />

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    style={{
                      width: '100%',
                      padding: '14px 24px',
                      backgroundColor: status === 'loading' ? '#a0aec0' : '#e53e3e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '500',
                      cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                      fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      if (status !== 'loading') e.target.style.backgroundColor = '#c53030';
                    }}
                    onMouseOut={(e) => {
                      if (status !== 'loading') e.target.style.backgroundColor = '#e53e3e';
                    }}
                  >
                    {status === 'loading' ? 'Unsubscribing...' : 'Unsubscribe'}
                  </button>
                </form>


                {/* Error Message */}
                {status === 'error' && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(229, 62, 62, 0.1)',
                    border: '1px solid rgba(229, 62, 62, 0.3)',
                    borderRadius: '8px',
                    color: '#c53030',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                  }}>
                    {message}
                  </div>
                )}

                {/* Back link */}
                <p style={{
                  marginTop: '2rem',
                  color: '#718096',
                  fontSize: '0.9rem',
                  fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                  textAlign: 'center',
                  marginBottom: 0
                }}>
                  <Link
                    to="/ai-notes"
                    style={{ color: '#667eea', textDecoration: 'none' }}
                    onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                  >
                    ← Back to AI Notes
                  </Link>
                </p>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default Unsubscribe;
